import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Automation, DoesAutomationHaveABrokenToken } from '@vendasta/automata';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { getImageSrc } from '../shared/imgsrc';
import { GalaxyNavControlService } from '@vendasta/galaxy/nav';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { AutomationViewService } from '../automation/automation-view.service';
import { AutomationService } from '../automation/automation.service';
import { TaskDefinitionsService } from '../data-services/task-definitions.service';
import { AutomationErrorHandlerService, WorkflowWarning } from './services';
import { TriggerService } from './trigger.service';
import { WorkflowStepService } from './workflow-step.service';
import { NodeActionsService } from './services/node-actions.service';
import { WorkflowViewerV2Component } from './workflow-viewer-v2/workflow-viewer-v2.component';
import { ChangeSideMenuContent } from './workflow-viewer-v2/workflow-viewer-v2.service';
import { AutomationsSideDrawerService } from './side-drawer/automations-side-drawer-service';
import { CurrentAutomationService } from '@galaxy/automata/shared';

@Component({
  selector: 'automata-automation-editor',
  templateUrl: './automation-editor.component.html',
  styleUrls: ['./automation-editor.component.scss'],
  providers: [TriggerService, WorkflowStepService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyEmptyStateModule,
    GalaxyPipesModule,
    GalaxyButtonGroupModule,
    GalaxyPageModule,
    MatSidenavModule,
    WorkflowViewerV2Component,
  ],
})
export class AutomationEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() readOnly = false;
  @Input() automationId = '';
  @Input() dontCloseNav = false;

  getImageSource = getImageSrc;

  automationId$: Observable<string> = this.route.paramMap.pipe(
    map((params) => {
      if (this.automationId) {
        return this.automationId;
      }
      return params.get('automationId');
    }),
  );

  automation$: Observable<Automation | null> = this.currentAutomationService.automation$;
  automationIsEditable$: Observable<boolean> = this.automation$.pipe(
    switchMap((a) => this.automationViewService.isEditable$(a)),
  );
  showMissingPermissions$: Observable<boolean> = this.automationViewService.showMissingPermissions$;

  showCopyWarningBanner$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  showCopyWarningBanner$: Observable<boolean> = this.showCopyWarningBanner$$.asObservable();

  automationLoadingError$$ = new Subject<Error>();
  automationLoadingError$ = this.automationLoadingError$$.asObservable();

  scrollToStart = true;
  @ViewChild('divToScroll') divToScroll: ElementRef;
  // setter will listen for changes to the div as it will initially be undefined due to it being nested
  // within multiple ng-templates that have ngIfs
  @ViewChild('divToFocusOnLoad') set divToFocusOnLoad(divToFocusOnLoad: ElementRef) {
    // will set 'scrollToStart' to false when any node button is clicked so that it doesn't refocus while editing
    if (divToFocusOnLoad && this.scrollToStart) {
      window.setTimeout(() => {
        this.divToScroll.nativeElement.scrollTo(divToFocusOnLoad.nativeElement.offsetLeft / 2 + 200, 0);
      }, 100);
    }
  }

  showErrorBanner$: Observable<boolean> = this.errorHandler.hasBlockingError$;
  showWarningBanner$: Observable<boolean> = this.errorHandler.hasBlockingWarning$;
  warnings$: Observable<WorkflowWarning[]> = this.errorHandler.workflowWarnings$;
  isPartnerViewingBuiltInAutomation$: Observable<boolean> =
    this.automationViewService.isPartnerViewingBuiltInAutomation();
  isTemplateView$: Observable<boolean> = this.automationViewService.isTemplateView();
  originBuiltInAutomation$: Observable<Automation> = this.automation$.pipe(
    switchMap((a) => {
      if (a?.originDefaultAutomationId) {
        return this.automationService.getOriginBuiltInAutomation(a.originDefaultAutomationId);
      }
      return EMPTY;
    }),
  );
  originBuiltInAutomationOn$: Observable<boolean> = this.automation$.pipe(
    switchMap((a) => {
      if (a?.originDefaultAutomationId) {
        return this.automationService.originBuiltInAutomationIsOn(a.namespace, a.context, a.originDefaultAutomationId);
      }
      return of(false);
    }),
  );
  tokenBroken$: Observable<boolean> = this.automation$.pipe(
    map((a) => {
      if (!a) {
        return false;
      }
      return DoesAutomationHaveABrokenToken(a);
    }),
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly automationViewService: AutomationViewService,
    private readonly errorHandler: AutomationErrorHandlerService,
    private readonly taskDefinitionsService: TaskDefinitionsService,
    private sideNavControl: GalaxyNavControlService,
    private readonly automationService: AutomationService,
    public readonly nodeActionsService: NodeActionsService,
    public readonly sideMenuService: AutomationsSideDrawerService,
    private readonly currentAutomationService: CurrentAutomationService,
  ) {}

  ngOnInit(): void {
    if (!this.dontCloseNav) {
      this.dontCloseNav = true; // prevent this from being called again when switching views
      this.sideNavControl.closeForThisPage();
    }

    if (this.route.snapshot.queryParams['email_to_user_emailId']) {
      combineLatest([this.automation$, this.automationIsEditable$])
        .pipe(take(1))
        .subscribe(([automation, isEditable]) => {
          this.sideMenuService.changeSideMenuContent(
            automation,
            isEditable,
            this.readOnly,
            this.route.snapshot.queryParams['email_to_user_emailId'],
            'editStep',
            '',
            '',
          );
        });
    }

    this.automationId$
      .pipe(
        tap((id) => {
          // Set up the services we'll primarily interact with
          this.currentAutomationService.init(id);
        }),
        take(1),
      )
      .subscribe();

    // preload all task definitions
    this.taskDefinitionsService.setPrecedingStepIds([]);
    this.taskDefinitionsService.allTaskDefinitions$.pipe(take(1)).subscribe();

    this.clearErrors();
  }

  ngAfterViewInit() {
    // Open the side menu if there is no trigger, this has to run after the sideMenu component has loaded
    this.automation$
      .pipe(
        filter((a) => !!a),
        tap((a) => {
          if (!a.triggerStep) {
            this.selectNewTrigger();
          }
        }),
        // If an error occurs while getting an automation, report it so the UI can appropriately choose error text
        catchError((err) => {
          console.error(err);
          this.automationLoadingError$$.next(err);
          return of();
        }),
        take(1),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    // We need to end grouping here when the component is destroyed so that it's
    // not carried over to the next automation. We want to by able to copy groups
    // and actions across automations, but not the creation of grouping.
    this.nodeActionsService.endGrouping();
  }

  clearErrors(): void {
    return this.errorHandler.clearValidationErrorMap();
  }

  selectNewTrigger(): void {
    combineLatest([this.automation$, this.automationIsEditable$])
      .pipe(take(1))
      .subscribe(([automation, isEditable]) => {
        this.sideMenuService.changeSideMenuContent(
          automation,
          isEditable,
          this.readOnly,
          '',
          'selectNewTrigger',
          '',
          '',
        );
      });
  }

  changeSideMenuContentEvent(event: ChangeSideMenuContent): void {
    combineLatest([this.automation$, this.automationIsEditable$])
      .pipe(take(1))
      .subscribe(([automation, isEditable]) => {
        this.sideMenuService.changeSideMenuContent(
          automation,
          isEditable,
          this.readOnly,
          event.requestingId,
          event.menuType,
          event.taskDefinitionId || '',
          '',
          event.precedingSteps,
          event.branch,
          event.groupId,
        );
      });
  }

  goTo(url: string): void {
    window.open(url, '_blank');
  }

  closeWarning(): void {
    this.showCopyWarningBanner$$.next(false);
  }

  goToSettings(): void {
    this.automationId$
      .pipe(
        take(1),
        tap(() => this.router.navigate(['settings'], { relativeTo: this.route })),
      )
      .subscribe();
  }

  isSettingsPage(): Observable<boolean> {
    return this.automationId$.pipe(
      take(1),
      map(() => this.router.isActive(this.router.createUrlTree(['settings'], { relativeTo: this.route }), false)),
    );
  }

  cancelGroup(): void {
    this.nodeActionsService.endGrouping();
  }

  submitGroup(): void {
    if (this.nodeActionsService.groupStepSize() < 2) return;

    this.nodeActionsService.submitGroup();
  }
}
